/**
 * Processing time notice management for product detail pages.
 * Handles special order status display, inventory tracking, and cart item updates.
 *
 * @class
 * @description This class provides:
 * - Processing time/estimated delivery notices on PDP
 * - Inventory quantity checking via API
 * - Cart item special order status management
 * - Variant-specific notice visibility
 *
 * @requires MultiStorefront (global API client)
 * @requires changeCartItem (global function)
 *
 * @example
 * // Automatically initializes on page load
 * let processingTimeNoticeClass = new processingTimeNotice();
 * processingTimeNoticeClass.init();
 */
class processingTimeNotice {

  /**
   * Creates a new processingTimeNotice instance.
   * @constructor
   */
  constructor() {
    /** @type {number|null} Current product ID */
    this.productId = null;
    /** @type {number|null} Current customer ID */
    this.customerId = null;
    /** @type {number|null} Current variant ID */
    this.currentVariantId = null;
    this.updating = false;
  }

  /**
   * Initializes the processing time notice functionality.
   * Checks for required DOM elements and sets up event listeners.
   *
   * @returns {void}
   * @sideEffect Sets productId, customerId, binds events, triggers inventory check
   */
  init() {
    // Exit if no special order container on page
    if (!document.querySelector('.special-order-container')) return;

    // Only run on product template
    if (document.querySelector('.template-product')) {
      this.productId = parseInt(document.querySelector('.product-form').dataset.productId);
      this.updateVariantId();
      this.bindEvents();
      this.runInventoryCheck();
      this.updateVariantShippingNotice();
    }

    // Get customer ID from window or body data
    if(typeof window.customerId != "undefined") {
      this.customerId = window.customerId;
    }
    else if(typeof document.querySelector('body').dataset.customerId != "undefined") {
      this.customerId = parseInt(document.querySelector('body').dataset.customerId);
    }
  }

  /**
   * Binds event listeners for quantity changes, variant changes, and cart updates.
   *
   * @returns {void}
   * @sideEffect Adds event listeners to quantity inputs, variant selectors, cart events
   */
  bindEvents() {
    let _this = this;

    if (document.querySelector('.product-purchase-form [name="id"]') != null) {
      document.querySelector('.product-purchase-form [name="id"]').addEventListener("change", (e) => {
          _this.updateVariantId();
          _this.runInventoryCheck(e);
      });
    }
    if (document.querySelector('.quantity-wrapper input[name="quantity"]') != null) {
      document.querySelectorAll('.quantity-wrapper input[name="quantity"]').forEach(function (el) {
        el.addEventListener("change", (e) => {
          _this.runInventoryCheck(e);
        });
      });
    }

    // Quantity +/- buttons
    if (document.querySelector('.quantity-wrapper a') != null) {
      document.querySelectorAll('.quantity-wrapper a').forEach(function (el) {
        el.addEventListener("click", (e) => {
          setTimeout(function () {
            _this.runInventoryCheck(e);
          }, 50)
        });
      });
    }

    document.addEventListener('theme:cartchanged', (e) => {
      _this.runInventoryCheck(e);
    });
  }

  /**
   * Runs an inventory check for the current product and updates variant inventory quantities.
   *
   * @param {Event|null} event - The event object triggering the inventory check or null if no event is provided.
   * @return {void} This method does not return a value.
   */
  runInventoryCheck(event = null) {
    if (this.updating) return;
    let _this = this;

    if(!this.customerId) return;

    this.updating = true;

    _this.getProcessingTimeNotice(_this.productId).then(
      (response) => {
        _this.updateVariantSelectInventoryQuantity(response.data);

        fetch('/cart.js')
          .then(res => res.json())
          .then(cartData => {
            _this.changeCartItemsSpecialOrderStatuses(cartData, response.data, event);
          });
      },
      (error) => {
        console.log('error', error)
      }
    ).catch(error => {
      console.log('error', error)
    });
  }

  /**
   * Updates the currentVariantId property from form input.
   *
   * @returns {void}
   * @sideEffect Sets currentVariantId from DOM input value
   */
  updateVariantId() {
    this.currentVariantId = typeof document.querySelector('.product-purchase-form [name="id"]') != "undefined" ?
      parseInt(document.querySelector('.product-purchase-form [name="id"]').value) : null;
  }

  /**
   * Fetches processing time notice data from the API for a given product.
   *
   * @param {string} productId - The product ID to fetch notice for
   * @returns {Promise<Object>} Resolves with notice data, rejects on error
   */
  getProcessingTimeNotice(productId) {
    if(!this.customerId) return;

    return new Promise(async (resolve, reject) => {
      if (!productId) {
        reject({
          data: null,
          message: 'Wrong/missing product ID',
          productId: null,
        });
      }

      MultiStorefront.init(window.api_base_url, window.auth_token);

      await MultiStorefront.get(`/inventory/${productId}`).then((response) => {
        if (typeof response.message != 'undefined') {
          reject({
            error: response,
            message: response.message,
            productId: productId,
          });
          return;
        }

        resolve({
          data: response,
          message: 'Success',
          productId: productId
        });
      })
    });
  }

  /**
   * Updates shipping notice input visibility based on enable flag.
   * Controls whether the delivery estimate is included in cart.
   *
   * @param {boolean|null} [enableInput=null] - Whether to enable the input
   * @returns {void}
   * @sideEffect Modifies name attribute on hidden input
   */
  updateVariantShippingNotice(enableInput = null) {
    let target = document.querySelector('.processing-time-notice-hidden-input');

    if (!target) return;

    let defaultTargetName = target.dataset.name;
    let targetName, targetName2;

    // Disable by removing name, enable by restoring it
    if (enableInput === false) {
      targetName = '';
    } else {
      // enable the input by restoring the name
      targetName = defaultTargetName;
    }
    target.setAttribute('name', targetName);
  }

  /**
   * Updates variant inventory quantity display and notice visibility.
   * Shows/hides special order notice based on base inventory vs quantity needed.
   *
   * @param {Object} data - Inventory data keyed by variant ID
   * @returns {void}
   * @sideEffect Toggles notice visibility, updates hidden input
   */
  updateVariantSelectInventoryQuantity(data) {
    let showNotice = false;
    let inputTarget = document.querySelector('.processing-time-notice-base-inventory');
    let noticeTarget = document.querySelector('.special-order-container');
    let isSpecialOrder = noticeTarget.classList.contains('is-special-order');
    let qty = parseInt(document.querySelector('[name="quantity"]').value);
    let baseQty = null;

    // Check base inventory for current variant
    for (const [key, value] of Object.entries(data)) {
      let varId = parseInt(key);
      if (varId === this.currentVariantId) {
        // Show notice if no base inventory or insufficient stock
        showNotice = typeof value.base == "undefined" || value.base < 1 || value.base < qty;
        baseQty = value.base;
      }
    }

    // Always show if marked as special order
    if (isSpecialOrder) showNotice = true;

    // Update base quantity input
    if (baseQty) {
      inputTarget.value = baseQty;
    }

    // Toggle notice visibility
    if (noticeTarget) {
      if (showNotice) {
        noticeTarget.classList.remove('hidden');
        this.updateVariantShippingNotice(true);
      } else {
        noticeTarget.classList.add('hidden');
        this.updateVariantShippingNotice(false);
      }
    }
  }

  /**
   * Updates the special order statuses and baseline inventory data for cart items by evaluating
   * inventory information and item properties. Adjusts cart items as necessary, including updating
   * item properties, handling special order notices, and refreshing the cart when changes are made.
   *
   * @param {Object} response - Response object containing cart items and their properties.
   * @param {Object} inventoryData - Object containing inventory details to validate and update cart items.
   * @param {Event|null} [event=null] - Optional event object, used to dispatch cart change notifications.
   * @return {void}
   */
  changeCartItemsSpecialOrderStatuses(response, inventoryData, event = null) {
    if (!response) return;

    let _this = this;
    let specialOrder = false;
    let haveSpecialOrder = false;
    let updatedItems = [];
    let promises = [];

    // First: update base inventory property if needed
    response.items.forEach((item) => {

      // First update the base inventory property where needed and check if we have a special order item
      if (inventoryData.hasOwnProperty(item.id)) {
        if (item.properties.hasOwnProperty('_base_inventory')) {
          let itemBaseInventory = parseInt(item.properties['_base_inventory']);
          let realBaseInventory = parseInt(inventoryData[item.id].base);

          let properties = item.properties;
          let update = false;

          // check if the line item is already a special order
          if (properties.hasOwnProperty('Est. Delivery')) {
            haveSpecialOrder = true;
            specialOrder = properties['Est. Delivery'];
          }
            // or update line item if the line item quantity is larger than base inventory
            // or if the base inventory line item property is not up to date
          else if (item.quantity > realBaseInventory) {
            specialOrder = document.querySelector('.processing-time-notice-hidden-input').value;
            haveSpecialOrder = true;
            properties['Est. Delivery'] = specialOrder;
            update = true;
          }
          if (itemBaseInventory != realBaseInventory) {
            properties['_base_inventory'] = realBaseInventory;
            update = true;
          }

          if (update) {
            updatedItems.push(item.id);
            let promise = changeCartItem({
              id: item.key,
              quantity: item.quantity,
              properties: properties
            });
            promises.push(promise);
          }
        }
      }
    });

    // Second: handle special order presence in cart
    if (haveSpecialOrder) {
      // add special order notice to all cart items
      response.items.forEach((item) => {

        let properties = item.properties;
        let update = false;

        // update line item if the est. delive isn't set, but should be,
        // or if the base inventory line item property is not up to date
        if (!updatedItems.includes(item.id) && !properties.hasOwnProperty('Est. Delivery')) {
          properties['Est. Delivery'] = specialOrder;
          update = true;
        }
        if (!updatedItems.includes(item.id) && inventoryData.hasOwnProperty(item.id)) {
          let itemBaseInventory = parseInt(item.properties['_base_inventory']);
          let realBaseInventory = parseInt(inventoryData[item.id].base);
          if(realBaseInventory != itemBaseInventory) {
            properties['_base_inventory'] = parseInt(inventoryData[item.id].base);
            update = true;
          }
        }

        if (update) {
          let promise = changeCartItem({
            id: item.key,
            quantity: item.quantity,
            properties: properties
          });
          promises.push(promise);
        }
      });
    } else {
      // remove special order notice from all cart items
      response.items.forEach((item) => {
        if (item.properties.hasOwnProperty('Est. Delivery')) {
          let properties = item.properties;
          properties['Est. Delivery'] = null;

          let promise = changeCartItem({
            id: item.key,
            quantity: item.quantity,
            properties: properties
          });

          promises.push(promise);
        }
      })
    }

    // After all updates complete, notify other components
    Promise.allSettled(promises).then(function (results) {
      setTimeout(function () {
        if (results.length > 0) {
          // Dispatch cart changed event
          if (event && event.bubbles) document.dispatchEvent(new CustomEvent('theme:cartchanged', {
            bubbles: false,
            cancelable: true
          }));
          // Open cart drawer
          document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', {bubbles: false, cancelable: false}));
        }
        _this.updating = false;
      }, 250);
    });
  }
}

// Create and initialize instance
let processingTimeNoticeClass = new processingTimeNotice();
processingTimeNoticeClass.init();